import React, { useEffect, useRef, useState } from 'react';
import { AddItemDetailsProp } from '../../types/item.mster.type';
import { Card, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import DataGrid, { Column, Scrolling, Summary, TotalItem } from 'devextreme-react/data-grid';
import { FieldConfig, productDetailFields, productHeaderFields, rawMaterial, RenderInput } from '../../model/FieldConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPenToSquare, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { data } from './index';
import { v4 as uuidv4 } from "uuid";
import { createItems, getItemMasterDropDown, getSubCategoryByCategoryId } from './services';
import { buildPayload, getTextFromList } from './services/buildPayload';
import notistack from 'pages/Core/Notistack';
import ConfirmCancelModal from './ConfirmCancelModal';
import ViewRawMaterialModal from './ViewRawMaterialModal';
import { HttpStatus } from 'pages/Core';
import { useNavigate } from 'react-router-dom';
import AddEditProductDetailModal from './AddEditProductDetailModal';
import AddEditRawMaterialModal from './AddEditRawMaterialModal';

const AddItem = () => {
    const navigate = useNavigate();

    // Separate form for product header and product details
    const headerForm = useForm<any>({
        mode: 'onChange'
    });

    const {
        register: registerHeader,
        handleSubmit: handleSubmitHeader,
        reset: resetHeader,
        control: controlHeader,
        watch: watchHeader,
        setValue: setValueHeader,
        getValues: getValuesHeader,
        setFocus,
        trigger: triggerHeader,
        formState: { errors: errorsHeader },
    } = headerForm;

    const [gridData, setGridData] = useState<any[]>([]);
    const [editingRowId, setEditingRowId] = useState<string | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const [rawMaterialsMap, setRawMaterialsMap] = useState<Map<string, any[]>>(new Map());
    const [selectedProductDetailId, setSelectedProductDetailId] = useState<string | null>(null);
    const [isViewDetailOpen, setIsViewDetailOpen] = useState(false);
    const [viewDetailId, setViewDetailId] = useState<string | null>(null);

    const [isProductDetailModalOpen, setIsProductDetailModalOpen] = useState(false);
    const [isAddingRawMaterialModalOpen, setIsAddingRawMaterialModalOpen] = useState(false);
    const [editingRawMaterialId, setEditingRawMaterialId] = useState<string | null>(null);

    const [savedHeaderValues, setSavedHeaderValues] = useState<any>(null);

    useEffect(() => {
        if (!savedHeaderValues) {
            const initialHeaderValues = headerFields.reduce((acc, field) => {
                acc[field.name] = "";
                return acc;
            }, {} as any);
            setSavedHeaderValues(initialHeaderValues);
            resetHeader(initialHeaderValues);
        }
    }, []);

    const handleHeaderChange = () => {
        const currentHeaderValues = getValuesHeader();
        setSavedHeaderValues(currentHeaderValues);
    };

    const handleAddProductDetail = (formData: any) => {
        if (editingRowId) {
            const updated = gridData.map(row =>
                row.id === editingRowId ? { ...row, ...formData } : row
            );
            setGridData(updated);
            setEditingRowId(null);
        } else {
            const newRow = {
                id: uuidv4(),
                ...formData,
            };
            setGridData(prev => [...prev, newRow]);
        }
        setIsProductDetailModalOpen(false);
    };

    const handleHeaderFieldChange = (fieldName: string, value: any) => {
        setValueHeader(fieldName, value);
        setSavedHeaderValues((prev: any) => ({
            ...prev,
            [fieldName]: value
        }));
    };

    const handleAddRawMaterial = (rawMaterialData: any) => {
        if (!selectedProductDetailId) return;

        if (editingRawMaterialId) {
            setRawMaterialsMap(prev => {
                const updated = new Map(prev);
                const existing = updated.get(selectedProductDetailId) || [];
                updated.set(selectedProductDetailId,
                    existing.map(rm => rm.id === editingRawMaterialId ? { ...rm, ...rawMaterialData } : rm)
                );
                return updated;
            });
            setEditingRawMaterialId(null);
        } else {
            const newRawMaterial = {
                id: uuidv4(),
                ...rawMaterialData,
            };

            setRawMaterialsMap(prev => {
                const updated = new Map(prev);
                const existing = updated.get(selectedProductDetailId) || [];
                updated.set(selectedProductDetailId, [...existing, newRawMaterial]);
                return updated;
            });
        }

        setIsAddingRawMaterialModalOpen(false);
        setSelectedProductDetailId(null);
    };

    const handleDeleteRawMaterial = (productDetailId: string, rawMaterialId: string) => {
        setRawMaterialsMap(prev => {
            const updated = new Map(prev);
            const existing = updated.get(productDetailId) || [];
            updated.set(productDetailId, existing.filter(rm => rm.id !== rawMaterialId));
            return updated;
        });
    };

    const validateHeaderFields = () => {
        const headerValues = getValuesHeader();
        const requiredFields = headerFields.filter(field => field.required);
        const missingFields = requiredFields.filter(field => {
            const value = headerValues[field.name];
            return !value || value === '' || value === null || value === undefined;
        });

        if (missingFields.length > 0) {
            return false;
        }
        return true;
    };

    const triggerHeaderValidation = async () => {
        const requiredFields = headerFields.filter(field => field.required);

        for (const field of requiredFields) {
            await triggerHeader(field.name);
        }

        const hasErrors = requiredFields.some(field => errorsHeader[field.name]);
        return !hasErrors;
    };

    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        const productsWithoutRawMaterials: number[] = [];
        gridData.forEach((gridItem, i: number) => {
            const itemRawMaterials = rawMaterialsMap.get(gridItem.id) || [];
            if (itemRawMaterials.length === 0) {
                productsWithoutRawMaterials.push(i + 1);
            }
        });

        if (productsWithoutRawMaterials.length > 0) {
            const productList = productsWithoutRawMaterials.join(', ');
            notistack.warning(
                `The following products don't have any raw materials assigned: ${productList}. ` +
                `Please add raw materials for these products before saving.`
            );
            return;
        }

        try {
            setIsSaving(true);

            const headerValues = getValuesHeader();

            const payloads = gridData.map(gridItem => {
                const itemRawMaterials = rawMaterialsMap.get(gridItem.id) || [];
                const combinedItem = {
                    ...headerValues,
                    ...gridItem
                };
                return buildPayload(combinedItem, combinedItem, itemRawMaterials, dropdowns);
            });

            const finalPayload = {
                productHeaderItemMaster: {
                    ...payloads[0].productHeaderItemMaster,
                },
                productDetailsModels: payloads.map((item) => ({
                    productDetailsItemMaster: {
                        ...item.productDetailsModels.productDetailsItemMaster,
                    },
                    itemMasterRawMaterials:
                        item.productDetailsModels?.itemMasterRawMaterials?.map((rm: any) => ({
                            ...rm,
                        })),
                })),
            };

            const response = await createItems(finalPayload);
            if (response.status === HttpStatus.Success) {
                notistack.notifyFromApi(response);
                navigate('/item-master');
            } else {
                notistack.notifyFromApi(response);
            }
        } catch (error: any) {
            notistack.error(error || "Failed to save item. Please try again.");
            console.error("Save failed:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const onEditRow = (row: any) => {
        setEditingRowId(row.id);
        setIsProductDetailModalOpen(true);
    };

    const onDeleteRow = (id: string) => {
        setGridData(gridData.filter(row => row.id !== id));
        setRawMaterialsMap(prev => {
            const updated = new Map(prev);
            updated.delete(id);
            return updated;
        });

        if (editingRowId === id) {
            setEditingRowId(null);
        }
    };

    const [headerFields, setHeaderFields] = useState<FieldConfig[]>(productHeaderFields);
    const [productDetailFields1, setProductDetailFields] = useState<FieldConfig[]>(productDetailFields);
    const [rawMaterialFields1, setRawMaterial] = useState<any>(rawMaterial);
    const [dropdowns, setDropdowns] = useState<{
        styleNumber: any[];
        materialType: any[];
        category: any[];
        metalColor: any[];
        karat: any[];
        complexity: any[];
        collection: any[];
        rowMaterialTypeList: any[];
    }>({
        styleNumber: [],
        materialType: [],
        category: [],
        metalColor: [],
        karat: [],
        collection: [],
        complexity: [],
        rowMaterialTypeList: []
    });

    useEffect(() => {
        fetchDropdowns();
    }, []);

    const mapOptions = (data: any[]) =>
        data?.map(item => ({
            label: item.text,
            value: item.value
        })) || [];

    const fetchDropdowns = async () => {
        try {
            const res = await getItemMasterDropDown();
            const data = res.data;

            setHeaderFields(prev =>
                prev.map(field => {
                    switch (field.name) {
                        case "styleNumber":
                            return { ...field, options: mapOptions(data.styleMaster) };
                        case "materialType":
                            return { ...field, options: mapOptions(data.materialType) };
                        case "category":
                            return { ...field, options: mapOptions(data.category) };
                        case "metalColor":
                            return { ...field, options: mapOptions(data.metalColoe) };
                        case "karat":
                            return { ...field, options: mapOptions(data.karat) };
                        case "collection":
                            return { ...field, options: mapOptions(data.collection) };
                        case "complexity":
                            return { ...field, options: mapOptions(data.complexity) };
                        default:
                            return field;
                    }
                })
            );

            setProductDetailFields(prev =>
                prev.map(field => {
                    switch (field.name) {
                        case "styleNumber":
                            return { ...field, options: mapOptions(data.styleMaster) };
                        case "materialType":
                            return { ...field, options: mapOptions(data.materialType) };
                        case "category":
                            return { ...field, options: mapOptions(data.category) };
                        case "metalColor":
                            return { ...field, options: mapOptions(data.metalColoe) };
                        case "karat":
                            return { ...field, options: mapOptions(data.karat) };
                        default:
                            return field;
                    }
                })
            );

            setRawMaterial((prev: any[]) =>
                prev.map(field => {
                    switch (field.name) {
                        case "rawkarat":
                            return { ...field, options: mapOptions(data.karat) };
                        case "rowType":
                            return { ...field, options: mapOptions(data.rowMaterialTypeList) };
                        default:
                            return field;
                    }
                })
            );

            setDropdowns({
                styleNumber: mapOptions(data.styleMaster),
                materialType: mapOptions(data.materialType),
                category: mapOptions(data.category),
                metalColor: mapOptions(data.metalColoe),
                karat: mapOptions(data.karat),
                collection: mapOptions(data.collection),
                complexity: mapOptions(data.complexity),
                rowMaterialTypeList: mapOptions(data.rowMaterialTypeList)
            });
        } catch (err) {
            console.error("Dropdown load failed", err);
        }
    };

    const selectedCategory = watchHeader("category");
    const [subCategoryOptions, setSubCategoryOptions] = useState<any[]>([]);

    useEffect(() => {
        if (!selectedCategory) {
            setSubCategoryOptions([]);

            setHeaderFields(prev =>
                prev.map(field =>
                    field.name === "subCategory"
                        ? { ...field, options: [] }
                        : field
                )
            );
            return;
        }

        const loadSubCategory = async () => {
            try {
                const res = await getSubCategoryByCategoryId(selectedCategory);
                const newOptions = mapOptions(res || []);

                setSubCategoryOptions(newOptions);

                setHeaderFields(prev =>
                    prev.map(field =>
                        field.name === "subCategory"
                            ? { ...field, options: newOptions }
                            : field
                    )
                );
            } catch (err) {
                console.error("Subcategory load failed", err);
                setSubCategoryOptions([]);
                setHeaderFields(prev =>
                    prev.map(field =>
                        field.name === "subCategory"
                            ? { ...field, options: [] }
                            : field
                    )
                );
            }
        };

        loadSubCategory();
    }, [selectedCategory]);

    const [showConfirmCancel, setShowConfirmCancel] = useState(false);

    const handleClose = () => {
        resetHeader();
        setGridData([]);
        setRawMaterialsMap(new Map());
        setIsAddingRawMaterialModalOpen(false);
        setEditingRawMaterialId(null);
        setSavedHeaderValues(null);
        navigate('/item-master');
    };

    const handleCancel = () => {
        setShowConfirmCancel(true);
    };

    return (
        <div className='page-content'>
            <div className="container-fluid">
                <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <h5>Add Item</h5>
                    </Card.Header>
                    <Card.Body>
                        <div
                            ref={scrollContainerRef}
                        >
                            <div className="w-100">
                                <div className="row g-2 p-2 border rounded mb-3">
                                    <div className="col-12 mb-1">
                                        <h5 className="mb-0">Product Header</h5>
                                    </div>

                                    {headerFields.map((f) => (
                                        <RenderInput
                                            key={f.name}
                                            placeholder={`Enter ${f.label}`}
                                            field={f}
                                            register={registerHeader}
                                            errors={errorsHeader}
                                            control={controlHeader}
                                            onChange={(value: any) => handleHeaderFieldChange(f.name, value)}
                                        />
                                    ))}
                                </div>

                                <div className="row mb-3">
                                    <div className="col-12">
                                        <button
                                            type="button"
                                            className="btn submit-btn"
                                            onClick={async () => {
                                                if (!validateHeaderFields()) {
                                                    await triggerHeaderValidation();
                                                    return;
                                                }
                                                setEditingRowId(null);
                                                setIsProductDetailModalOpen(true);
                                            }}
                                        >
                                            Add Product Details
                                        </button>
                                    </div>
                                </div>

                                {gridData.length > 0 && (
                                    <div className="row g-2 p-2 border rounded mb-2">
                                        <div className="col-12 mb-2 d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0">Added Product Details</h5>
                                        </div>

                                        <div className="col-12">
                                            <DataGrid
                                                dataSource={gridData}
                                                showRowLines
                                                showBorders
                                                keyExpr="id"
                                            >
                                                <Scrolling
                                                    mode="standard"
                                                    useNative
                                                    scrollByContent
                                                    scrollByThumb
                                                />

                                                <Summary>
                                                    <TotalItem />
                                                </Summary>

                                                <Column
                                                    caption="Action"
                                                    width={180}
                                                    cssClass="text-center"
                                                    cellRender={({ data }) => (
                                                        <div className="d-flex justify-content-center align-items-center gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm action-btn delete-btn"
                                                                title="Delete"
                                                                onClick={() => onDeleteRow(data.id)}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="btn btn-sm action-btn edit-btn"
                                                                title="Edit"
                                                                onClick={() => onEditRow(data)}
                                                            >
                                                                <FontAwesomeIcon icon={faPenToSquare} />
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="btn btn-sm action-btn view-btn"
                                                                title="View Raw Materials"
                                                                onClick={() => {
                                                                    setViewDetailId(data.id);
                                                                    setIsViewDetailOpen(true);
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="btn btn-sm action-btn add-btn"
                                                                title="Add Raw Material"
                                                                onClick={() => {
                                                                    setSelectedProductDetailId(data.id);
                                                                    setEditingRawMaterialId(null);
                                                                    setIsAddingRawMaterialModalOpen(true);
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faPlus} />
                                                            </button>
                                                        </div>
                                                    )}
                                                />

                                                <Column
                                                    dataField="size"
                                                    caption="Size"
                                                    width={100}
                                                    dataType="string"
                                                />

                                                <Column
                                                    dataField="weight"
                                                    caption="Gross Wt"
                                                    width={100}
                                                    dataType="string"
                                                />

                                                <Column
                                                    dataField="karat"
                                                    caption="Karat"
                                                    width={150}
                                                    dataType="string"
                                                    cellRender={({ data }) => {
                                                        return <span>{getTextFromList(data.karat, dropdowns.karat)}</span>;
                                                    }}
                                                />

                                                <Column
                                                    dataField="diamondQty"
                                                    caption="Diamond Qty"
                                                    width={150}
                                                    dataType="string"
                                                />

                                                <Column
                                                    dataField="netWeight"
                                                    caption="Net Wt"
                                                    width={150}
                                                    dataType="string"
                                                />

                                                <Column
                                                    dataField="stoneType"
                                                    caption="Stone Type"
                                                    width={150}
                                                    dataType="string"
                                                />

                                                <Column
                                                    dataField="makingType"
                                                    caption="Making Type"
                                                    width={150}
                                                    dataType="string"
                                                />

                                                <Column
                                                    dataField="collorStoneWeight"
                                                    caption="Color Stone Wt"
                                                    width={150}
                                                    dataType="string"
                                                />
                                                <Column
                                                    dataField="tag"
                                                    caption="Tag"
                                                    width={150}
                                                    dataType="string"
                                                />

                                                <Column
                                                    caption="Raw Materials"
                                                    width={120}
                                                    alignment="center"
                                                    cellRender={({ data }) => {
                                                        const count = rawMaterialsMap.get(data.id)?.length || 0;
                                                        return (
                                                            <span className={`badge ${count > 0 ? 'bg-success' : 'bg-secondary'}`}>
                                                                {count} Items
                                                            </span>
                                                        );
                                                    }}
                                                />
                                            </DataGrid>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-12 d-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn add-btn me-2"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </button>

                                {gridData.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn submit-btn ms-2"
                                        disabled={isSaving}
                                        onClick={handleSave}
                                    >
                                        Save
                                    </button>
                                )}
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </div>

            <AddEditProductDetailModal
                show={isProductDetailModalOpen}
                onClose={() => {
                    setIsProductDetailModalOpen(false);
                    setEditingRowId(null);
                }}
                onSubmit={handleAddProductDetail}
                editingRow={gridData.find(row => row.id === editingRowId)}
                productDetailFields={productDetailFields1}
                dropdowns={dropdowns}
            />

            <AddEditRawMaterialModal
                show={isAddingRawMaterialModalOpen}
                onClose={() => {
                    setIsAddingRawMaterialModalOpen(false);
                    setSelectedProductDetailId(null);
                    setEditingRawMaterialId(null);
                }}
                onSubmit={handleAddRawMaterial}
                editingRawMaterial={editingRawMaterialId && selectedProductDetailId ?
                    rawMaterialsMap.get(selectedProductDetailId)?.find(rm => rm.id === editingRawMaterialId) : null}
                rawMaterialFields={rawMaterialFields1}
            />

            <ViewRawMaterialModal
                show={isViewDetailOpen}
                onClose={() => {
                    setIsViewDetailOpen(false);
                    setViewDetailId(null);
                }}
                rawMaterials={viewDetailId ? rawMaterialsMap.get(viewDetailId) || [] : []}
                dropdowns={dropdowns}
                onDelete={(rawMaterialId) => {
                    if (viewDetailId) {
                        handleDeleteRawMaterial(viewDetailId, rawMaterialId);
                    }
                }}
            />

            <ConfirmCancelModal
                show={showConfirmCancel}
                onCancel={() => setShowConfirmCancel(false)}
                onConfirm={() => {
                    setShowConfirmCancel(false);
                    handleClose();
                }}
                title="Discard Changes?"
                message="You have unsaved changes. Are you sure you want to cancel?"
            />
        </div>
    );
};

export default AddItem;